import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { api } from '../../lib/api/client.js';
import type { LiveExtension } from '@arcnet/contracts';
import { useLiveScope, useLiveState } from './useLiveState.js';
import { useRealtime } from '../../lib/api/realtime.js';
import {
  TableSkeleton, Empty, ErrorBanner, Badge, Stat, StatGroup,
} from '../../components/ui/Primitives.js';
import { Icon } from '../../components/ui/Icon.js';
import { isUnreachable, pingDetail } from '../extensions/ExtensionPresence.js';
import { relativeTime, formatDuration } from '../../lib/format.js';

/**
 * Every seat on the platform, live.
 *
 * The operator's view rather than a customer's: one table across every account,
 * sorted so anything wrong floats to the top. It exists because "is anything
 * broken right now" is a question nobody should have to answer by opening
 * thirty accounts in turn.
 *
 * Updates arrive on the live stream: a phone registering anywhere refreshes
 * this within about a second, and a call shows the moment it rings.
 */

/** A row of /v1/live/extensions: the seat, not its call state. */
interface LiveSeat {
  id: string; number: string; display_name: string; status: string;
  do_not_disturb: boolean;
  tenant_id: string; tenant_name: string; sip_domain: string;
  admin_group_name: string | null;
  registered: boolean;
  contact_ip: string | null; contact_port: number | null;
  transport: string | null; user_agent: string | null;
  ping_status?: string | null; ping_ms?: number | null;
  last_seen_at: string | null; offline_since: string | null;
  ever_seen: boolean;
}

/** Registered and answering pings: reachable by a caller (ExtensionPresence.tsx). */
const isOnline = (e: LiveSeat): boolean => e.registered && !isUnreachable(e.ping_status);
const isOffline = (e: LiveSeat): boolean => (e.ever_seen && !e.registered) || (e.registered && isUnreachable(e.ping_status));

type Filter = 'all' | 'online' | 'offline' | 'never';

/**
 * Call state where there is any, presence where there is not.
 *
 * "On a call" implies registered, so showing both would spend a column saying
 * the same thing twice.
 */
function CallBadge({ live, registered, everSeen, unreachable, detail }: {
  live: LiveExtension | null; registered: boolean; everSeen: boolean;
  unreachable: boolean; detail: string;
}) {
  if (live?.state === 'on_call') {
    return (
      <Badge tone="info">
        <span className="arc-dot" aria-hidden="true" />
        on a call{live.direction ? ` · ${live.direction}` : ''}
      </Badge>
    );
  }
  if (live?.state === 'ringing') {
    return (
      <Badge tone="warn">
        <span className="arc-dot arc-pulse" aria-hidden="true" />ringing
      </Badge>
    );
  }
  if (registered && unreachable) {
    return <Badge tone="danger"><span className="arc-dot" aria-hidden="true" />unreachable</Badge>;
  }
  if (registered) {
    return (
      <>
        <Badge tone="ok"><span className="arc-dot" aria-hidden="true" />online</Badge>
        {detail && <span className="arc-subtle arc-num" style={{ fontSize: 11.5, marginLeft: 6 }}>{detail}</span>}
      </>
    );
  }
  if (everSeen) {
    return <Badge tone="danger"><span className="arc-dot" aria-hidden="true" />offline</Badge>;
  }
  return <Badge tone="muted">never seen</Badge>;
}

export function LivePage() {
  const nav = useNavigate();
  const [filter, setFilter] = useState<Filter>('all');
  const [q, setQ] = useState('');

  /**
   * Call state from the calls channel, presence from the registrations one.
   * Both ride the tab's single socket; the queues channel is not asked for,
   * because nothing on this page shows a queue.
   */
  const { platform, tenantId } = useLiveScope();
  const { extensions: liveExtensions, connection: live } = useLiveState(['calls']);
  useRealtime(tenantId, { platform });

  const callByExt = useMemo(() => {
    const m = new Map<string, LiveExtension>();
    for (const e of liveExtensions) m.set(`${e.user}@${e.domain}`, e);
    return m;
  }, [liveExtensions]);

  const { data, isLoading, error } = useQuery({
    // One account when there is one to show; the API checks it is in the caller's scope.
    queryKey: ['live-extensions', tenantId],
    queryFn: () => api.get<{ data: LiveSeat[] }>(`/v1/live/extensions${tenantId ? `?tenant_id=${tenantId}` : ''}`),
    // A safety net only. The event stream is what keeps this current; without
    // the net, a dropped stream would leave the page silently frozen.
    refetchInterval: 60_000,
  });

  const all = data?.data ?? [];
  const counts = useMemo(() => ({
    total: all.length,
    online: all.filter(isOnline).length,
    offline: all.filter(isOffline).length,
    never: all.filter((e) => !e.ever_seen).length,
    tenants: new Set(all.map((e) => e.tenant_id)).size,
  }), [all]);

  const rows = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return all.filter((e) => {
      if (filter === 'online' && !isOnline(e)) return false;
      if (filter === 'offline' && !isOffline(e)) return false;
      if (filter === 'never' && e.ever_seen) return false;
      if (!needle) return true;
      return e.number.includes(needle)
        || e.display_name.toLowerCase().includes(needle)
        || e.tenant_name.toLowerCase().includes(needle)
        || (e.user_agent ?? '').toLowerCase().includes(needle)
        || (e.contact_ip ?? '').includes(needle);
    });
  }, [all, filter, q]);

  return (
    <>
      <div className="arc-page-head">
        <div>
          <h1 className="arc-h1">{platform ? 'Live' : 'Live extensions'}</h1>
          <p>{platform ? 'Every extension on the platform, as it happens' : 'Every extension in this account, as it happens'}</p>
        </div>
        <div className="arc-row">
          <Badge tone={live === 'live' ? 'ok' : 'warn'}>
            <span className="arc-dot" aria-hidden="true" />
            {live === 'live' ? 'live' : live === 'connecting' ? 'connecting' : 'reconnecting'}
          </Badge>
        </div>
      </div>

      {error && <ErrorBanner error={error} />}

      <div style={{ marginBottom: 14 }}>
        <StatGroup>
          <Stat bare icon="extension" hue={1} label="Seats" value={counts.total}
            sub={platform ? `across ${counts.tenants} account${counts.tenants === 1 ? '' : 's'}` : 'in this account'} />
          <Stat bare icon="phone" hue={2} label="Online" value={counts.online}
            sub="handset registered" />
          <Stat bare icon="warning" hue={3} label="Offline" value={counts.offline}
            tone={counts.offline > 0 ? 'warn' : undefined}
            sub={counts.offline > 0 ? 'was registered, now gone' : 'nothing down'} />
          <Stat bare icon="server" hue={4} label="Never seen" value={counts.never}
            sub="no handset yet" />
        </StatGroup>
      </div>

      {/* The queues table that used to sit here now has its own page under
          Realtime. It was a secondary card beneath a heading that said "every
          extension on the platform", which is not where anyone looks to find
          out whether callers are on hold. */}

      <div className="arc-card">
        <div className="arc-toolbar">
          <div className="arc-search">
            <Icon name="search" size={14} />
            <input className="arc-input" placeholder="Account, extension, handset or IP…"
              value={q} onChange={(e) => setQ(e.target.value)}
              aria-label="Search live extensions" />
          </div>
          <div className="arc-row" style={{ gap: 2 }}>
            {(['all', 'online', 'offline', 'never'] as const).map((f) => (
              <button key={f} type="button"
                className={`arc-btn arc-btn-sm ${filter === f ? 'arc-btn-primary' : 'arc-btn-ghost'}`}
                onClick={() => setFilter(f)}>
                {f === 'never' ? 'never seen' : f}
              </button>
            ))}
          </div>
          <div className="arc-spacer" />
          <span className="arc-subtle arc-num" style={{ fontSize: 12.5 }}>
            {rows.length === all.length ? all.length : `${rows.length} of ${all.length}`} shown
          </span>
        </div>

        <div className="arc-table-wrap">
          <table className="arc-table">
            <thead>
              <tr>
                {platform && <th>Account</th>}<th>Extension</th><th>Presence</th>
                <th>Handset</th><th>Address</th><th>Last seen</th>
              </tr>
            </thead>
            {isLoading ? <TableSkeleton cols={platform ? 6 : 5} /> : (
              <tbody>
                {rows.map((e) => (
                  <tr key={e.id} className="arc-clickable"
                    onClick={() => {
                      // Drills into the seat inside its own account, which is
                      // where every setting and the full registration history
                      // already live.
                      void nav({
                        to: '/manage/$tenantId/$',
                        params: { tenantId: e.tenant_id, _splat: `extensions/${e.number}` },
                      });
                    }}>
                    {platform && <td style={{ fontWeight: 550 }}>{e.tenant_name}</td>}
                    <td>
                      <div className="arc-row" style={{ gap: 10 }}>
                        <span className="arc-ext-num">{e.number}</span>
                        <span className="arc-truncate">{e.display_name}</span>
                      </div>
                    </td>
                    <td>
                      <CallBadge
                        live={callByExt.get(`${e.number}@${e.sip_domain}`) ?? null}
                        registered={e.registered} everSeen={e.ever_seen}
                        unreachable={isUnreachable(e.ping_status)}
                        detail={pingDetail(e.ping_status, e.ping_ms)} />
                      {e.do_not_disturb && <Badge tone="warn">DND</Badge>}
                    </td>
                    <td className="arc-muted arc-truncate" style={{ maxWidth: 190 }}>
                      {e.user_agent ?? <span className="arc-subtle">—</span>}
                    </td>
                    <td className="arc-mono arc-muted" style={{ fontSize: 11.5 }}>
                      {e.contact_ip
                        ? `${e.contact_ip}${e.contact_port ? ':' + e.contact_port : ''}`
                        : <span className="arc-subtle">—</span>}
                      {e.transport && (
                        <span className="arc-subtle" style={{ marginLeft: 6 }}>
                          {e.transport.toUpperCase()}
                        </span>
                      )}
                    </td>
                    <td className="arc-muted">
                      {e.last_seen_at
                        ? relativeTime(e.last_seen_at)
                        : <span className="arc-subtle">never</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            )}
          </table>
        </div>

        {data !== undefined && all.length === 0 && (
          <Empty icon="activity" title="No extensions yet"
            hint="Seats appear here as soon as an account has one." />
        )}
        {data !== undefined && all.length > 0 && rows.length === 0 && (
          <Empty icon="search" title="Nothing matches"
            hint="Try a different filter or search term." />
        )}
      </div>
    </>
  );
}
